import { useCallback, useMemo } from 'react';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { getAllInPriceFromListPriceForListing } from 'src/utils/inventoryUtils';
import {
  BulkListingPriceCalculation,
  CompListing,
  Listing,
  ListingBroadcastStatus,
} from 'src/WebApiController';

import { ComparableListing } from '../ComparableListings';

export const usePricingCalculationListings = (
  listings: Listing[],
  selectedListing: Listing | null,
  selectedListingIds: number[],
  compListings: CompListing[],
  listingPriceCalculations: BulkListingPriceCalculation[]
) => {
  const query = useSyncCenterSettings();
  const showUnbroadcastListingsInComps = useMemo(
    () => Boolean(query.data?.showUnbroadcastListingsInComps),
    [query.data?.showUnbroadcastListingsInComps]
  );

  const sanitizedSelectedListingIds = useMemo(() => {
    if (selectedListing) {
      return [selectedListing.id];
    }
    return selectedListingIds;
  }, [selectedListing, selectedListingIds]);

  const listingPriceCalculationsByListingId = useMemo(() => {
    const record: Record<number, BulkListingPriceCalculation> = {};
    listingPriceCalculations.forEach((x) => {
      record[x.listingId] = x;
    });

    return record;
  }, [listingPriceCalculations]);

  const selectedListingPriceCalculation = useMemo(
    () =>
      selectedListing
        ? listingPriceCalculationsByListingId[selectedListing.id]
        : null,
    [listingPriceCalculationsByListingId, selectedListing]
  );

  const mapListingToComparableListing = useCallback(
    (listing: Listing): ComparableListing => {
      const listingPreview = listingPriceCalculationsByListingId[listing.id];

      const websitePrice =
        listingPreview?.websitePrice?.amt ??
        getAllInPriceFromListPriceForListing(listing);

      const autoPriceResultType = listingPreview?.autoPricingResultType;
      const autoPricingResultConstraintType =
        listingPreview?.autoPricingConstraintType;

      return {
        listingId: listing.id,
        section: listing.seating.section,
        row: listing.seating.row ?? undefined,
        seatFr: listing.seating.seatFr ?? undefined,
        seatTo: listing.seating.seatTo ?? undefined,
        price: websitePrice,
        unitCost: listing.unitCst?.amt ?? undefined,
        currencyCode: listing.currency,
        listingNotes: listing.seatTraits ?? undefined,
        // Price assuming same fees as seller’s account
        sellerNetProceeds: listing.listPrice,
        isYourListing: sanitizedSelectedListingIds.includes(listing.id),
        isSellerListing: true,
        isListed: listing.brdcstStatus === ListingBroadcastStatus.Broadcast,
        availableTickets: listing.availQty,
        validPurchaseQuantities: listing.vldPurQty,
        isSelectedWithListingGroup: false,
        autoPricingResultType: autoPriceResultType ?? undefined,
        autoPricingResultConstraintType:
          autoPricingResultConstraintType ?? undefined,
        shouldShowResultTypeAction: listing.isAutoPrc ?? false,
      };
    },
    [listingPriceCalculationsByListingId, sanitizedSelectedListingIds]
  );

  const mapCompListingToComparableListing = useCallback(
    (cl: CompListing): ComparableListing => {
      const isAnchor = selectedListingPriceCalculation?.udrRefLstIds?.includes(
        cl.listingId
      );
      const isOutlier =
        selectedListingPriceCalculation?.outlierStubHubListingIds.includes(
          cl.listingId
        ) ?? false;

      return {
        listingId: cl.listingId,
        section: cl.section ?? undefined,
        row: cl.row ?? undefined,
        seatFr: cl.seatFrom ?? undefined,
        seatTo: cl.seatTo ?? undefined,
        price: cl.sellerAllInPrice?.amt,
        priceDisp: cl.sellerAllInPrice?.disp,
        currencyCode: cl.sellerAllInPrice?.currency ?? undefined,
        listingNotes: cl.listingNotes ?? undefined,
        // Price assuming same fees as seller’s account
        sellerNetProceeds: cl.sellerNetProceeds?.amt ?? undefined,
        isAnchor: isAnchor,
        isOutlier: isOutlier,
        isYourListing: false,
        isSellerListing: false,
        isFanInventory: cl.isFanInventory ?? false,
        availableTickets: cl.availableTickets ?? undefined,
        validPurchaseQuantities: cl.validPurchaseQuantities ?? undefined,
        isSelectedWithListingGroup: false,
      };
    },
    [
      selectedListingPriceCalculation?.outlierStubHubListingIds,
      selectedListingPriceCalculation?.udrRefLstIds,
    ]
  );

  const sellerAccountPosListingIds = useMemo(() => {
    if (selectedListingPriceCalculation) {
      return new Set(
        selectedListingPriceCalculation.sellerAccountPosListingIds
      );
    }
    return new Set(
      listingPriceCalculations.flatMap((c) => c.sellerAccountPosListingIds)
    );
  }, [listingPriceCalculations, selectedListingPriceCalculation]);

  const filterSellerListings = useCallback(
    (listing: Listing): boolean => {
      // Always include the selected listing
      if (sanitizedSelectedListingIds.includes(listing.id)) {
        return true;
      }

      // Only include listings that are in sellerAccountPosListingIds
      if (!sellerAccountPosListingIds.has(listing.id)) {
        return false;
      }

      // Exclude listings with zero availability
      if (listing.availQty <= 0) {
        return false;
      }

      // Exclude unbroadcast listings if the setting is off
      if (!showUnbroadcastListingsInComps) {
        return listing.brdcstStatus === ListingBroadcastStatus.Broadcast;
      }

      return true;
    },
    [
      sanitizedSelectedListingIds,
      sellerAccountPosListingIds,
      showUnbroadcastListingsInComps,
    ]
  );

  const sellerListings = useMemo(() => {
    return listings
      .filter(filterSellerListings)
      .map(mapListingToComparableListing);
  }, [filterSellerListings, listings, mapListingToComparableListing]);

  const isComparableToSelectedListing = useCallback(
    (cl: CompListing) => {
      if (cl.isSellerCompListing) {
        return (
          selectedListingPriceCalculation?.sellerCompStubHubListingIds.includes(
            cl.listingId
          ) ?? false
        );
      }

      const isComparableListing =
        selectedListingPriceCalculation?.compStubHubListingIds.includes(
          cl.listingId
        ) ?? false;
      const isOutlierListing =
        selectedListingPriceCalculation?.outlierStubHubListingIds.includes(
          cl.listingId
        ) ?? false;

      return !selectedListing || isComparableListing || isOutlierListing;
    },
    [
      selectedListing,
      selectedListingPriceCalculation?.compStubHubListingIds,
      selectedListingPriceCalculation?.outlierStubHubListingIds,
      selectedListingPriceCalculation?.sellerCompStubHubListingIds,
    ]
  );

  const comparableListings = useMemo(() => {
    return compListings
      .filter(isComparableToSelectedListing)
      .map(mapCompListingToComparableListing);
  }, [
    compListings,
    isComparableToSelectedListing,
    mapCompListingToComparableListing,
  ]);

  return {
    sellerListings,
    comparableListings,
    selectedListingPriceCalculation,
  };
};
